import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useState } from "react";

import type {
  CreateHarnessOrderResponse,
  HarnessDiagnosticsResponse,
  HarnessTransitionResult,
  RunHarnessTransitionsResponse,
} from "#/api/medicationOrderHarness.api";
import BooleanIcon from "#/components/shared/details/BooleanIcon";
import { cn } from "#/components/utls/utils";
import useMedicationHarnessRun from "#/hooks/useMedicationHarnessRun";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MEDICATION_PRESETS = [
  "Amoxicillin 500 mg",
  "Lisinopril 10 mg",
  "Metformin 500 mg",
  "Atorvastatin 20 mg",
  "Levothyroxine 50 mcg",
] as const;

const HAPPY_PATH_STEPS = [
  { step: "submit_for_review" as const },
  { step: "approve" as const },
  { step: "queue_for_dispatch" as const },
];

type RunState = "idle" | "running" | "success" | "failed";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isValidUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value
  );
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function UserPicker({
  userId,
  onSelect,
  disabled,
  userSearchMutation,
}: {
  userId: string;
  onSelect: (id: string) => void;
  disabled: boolean;
  userSearchMutation: ReturnType<
    typeof useMedicationHarnessRun
  >["userSearchMutation"];
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<
    { id: string; label: string; email?: string }[]
  >([]);
  const [searchError, setSearchError] = useState<string | null>(null);

  async function handleSearch() {
    if (!query.trim()) return;
    setSearchError(null);
    try {
      // If the query looks like a UUID, select it directly
      if (isValidUuid(query.trim())) {
        onSelect(query.trim());
        setResults([]);
        return;
      }
      const users = await userSearchMutation.mutateAsync({
        email: query.includes("@") ? query : null,
        firstName: !query.includes("@") ? query : null,
      });
      setResults(
        users.map((u) => ({
          id: u.id,
          label: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.id,
          email: u.email ?? undefined,
        }))
      );
    } catch (err: any) {
      setSearchError(err?.message || "Search failed");
    }
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-brown-700">
        User ID
      </label>
      <div className="flex gap-2">
        <input
          className="flex-1 rounded-lg border border-cream-300 px-3 py-2 text-sm"
          placeholder="Search by name / email or paste UUID"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          disabled={disabled}
        />
        <button
          type="button"
          className="rounded-lg border border-cream-300 bg-cream-50 px-3 py-2 text-sm text-brown-700 hover:bg-cream-100 disabled:opacity-60"
          onClick={handleSearch}
          disabled={
            disabled || userSearchMutation.isPending || !query.trim()
          }
        >
          {userSearchMutation.isPending ? "Searching…" : "Search"}
        </button>
      </div>

      {userId && (
        <p className="flex items-center gap-1 text-xs text-brown-600">
          Selected:{" "}
          <code className="rounded bg-cream-100 px-1.5 py-0.5 font-mono text-xs">
            {userId}
          </code>
          <button
            type="button"
            className="ml-1 text-brown-400 hover:text-brown-600"
            onClick={() => copyToClipboard(userId)}
            title="Copy user ID"
          >
            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
          </button>
        </p>
      )}

      {searchError && (
        <p className="text-xs text-red-600">{searchError}</p>
      )}

      {results.length > 0 && (
        <ul className="max-h-40 overflow-y-auto rounded-lg border border-cream-200 bg-white">
          {results.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                className={cn(
                  "w-full px-3 py-2 text-left text-sm hover:bg-cream-100",
                  u.id === userId && "bg-terracotta-50 font-medium"
                )}
                onClick={() => {
                  onSelect(u.id);
                  setResults([]);
                  setQuery("");
                }}
              >
                <span className="text-brown-800">{u.label}</span>
                {u.email && (
                  <span className="ml-2 text-xs text-brown-400">
                    {u.email}
                  </span>
                )}
                <span className="ml-2 font-mono text-xs text-brown-400">
                  {u.id.slice(0, 8)}…
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MedicationChips({
  selected,
  onSelect,
  disabled,
}: {
  selected: string;
  onSelect: (name: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-brown-700">
        Medication
      </label>
      <div className="flex flex-wrap gap-2">
        {MEDICATION_PRESETS.map((med) => (
          <button
            key={med}
            type="button"
            disabled={disabled}
            className={cn(
              "rounded-full border px-3 py-1.5 text-sm transition-colors",
              selected === med
                ? "border-terracotta-400 bg-terracotta-100 font-medium text-terracotta-800"
                : "border-cream-300 bg-white text-brown-600 hover:bg-cream-100"
            )}
            onClick={() => onSelect(med)}
          >
            {med}
          </button>
        ))}
      </div>
    </div>
  );
}

function TransitionTimeline({
  results,
}: {
  results: HarnessTransitionResult[];
}) {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-brown-800">
        Transition Timeline
      </h3>
      <ol className="relative ml-3 border-l border-cream-300">
        {results.map((r, i) => {
          const isSuccess = r.status === "success";
          const isFailed = r.status === "failed";
          return (
            <li key={i} className="mb-4 ml-6">
              <span
                className={cn(
                  "absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full",
                  isSuccess && "bg-emerald-100",
                  isFailed && "bg-red-100",
                  !isSuccess && !isFailed && "bg-cream-200"
                )}
              >
                {isSuccess && (
                  <CheckCircleIcon className="h-4 w-4 text-emerald-600" />
                )}
                {isFailed && (
                  <XCircleIcon className="h-4 w-4 text-red-600" />
                )}
                {!isSuccess && !isFailed && (
                  <span className="h-2 w-2 rounded-full bg-cream-400" />
                )}
              </span>
              <div>
                <p className="text-sm font-medium text-brown-800">
                  {r.step.replace(/_/g, " ")}
                </p>
                <span
                  className={cn(
                    "inline-block rounded px-1.5 py-0.5 text-xs font-medium",
                    isSuccess && "bg-emerald-50 text-emerald-700",
                    isFailed && "bg-red-50 text-red-700",
                    !isSuccess && !isFailed && "bg-cream-100 text-brown-500"
                  )}
                >
                  {r.status}
                </span>
                {r.error && (
                  <pre className="mt-1 overflow-x-auto rounded bg-red-50 p-2 text-xs text-red-700">
                    {typeof r.error === "string"
                      ? r.error
                      : JSON.stringify(r.error, null, 2)}
                  </pre>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function DiagnosticsPanel({
  diagnostics,
  orderId,
}: {
  diagnostics: HarnessDiagnosticsResponse;
  orderId: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const correlationIds = diagnostics.correlation_ids ?? [];
  const idempotencyKeys = diagnostics.idempotency_keys ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-brown-800">Diagnostics</h3>
        <button
          type="button"
          className="text-xs text-brown-500 hover:text-brown-700"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "Collapse" : "Expand"} raw JSON
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {orderId && (
          <div className="rounded-lg border border-cream-200 bg-cream-50 p-3">
            <p className="text-xs text-brown-500">Order ID</p>
            <div className="flex items-center gap-1">
              <code className="text-xs text-brown-700">{orderId}</code>
              <button
                type="button"
                className="text-brown-400 hover:text-brown-600"
                onClick={() => copyToClipboard(orderId)}
              >
                <ClipboardDocumentIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}

        {correlationIds.length > 0 && (
          <div className="rounded-lg border border-cream-200 bg-cream-50 p-3">
            <p className="text-xs text-brown-500">Correlation IDs</p>
            {correlationIds.map((cid, i) => (
              <div key={i} className="flex items-center gap-1">
                <code className="text-xs text-brown-700">{cid}</code>
                <button
                  type="button"
                  className="text-brown-400 hover:text-brown-600"
                  onClick={() => copyToClipboard(cid)}
                >
                  <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {idempotencyKeys.length > 0 && (
          <div className="rounded-lg border border-cream-200 bg-cream-50 p-3">
            <p className="text-xs text-brown-500">Idempotency Keys</p>
            {idempotencyKeys.map((key, i) => (
              <code key={i} className="block text-xs text-brown-700">
                {key}
              </code>
            ))}
          </div>
        )}
      </div>

      {isExpanded && (
        <pre className="max-h-96 overflow-auto rounded-lg bg-cream-50 p-3 text-xs text-brown-800">
          {JSON.stringify(diagnostics, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export default function AdminMedicationOrderHarnessPage() {
  // Hook-based API layer
  const {
    createOrderMutation,
    runTransitionsMutation,
    diagnosticsMutation,
    userSearchMutation,
    queueStatusQuery,
    isRunning,
  } = useMedicationHarnessRun();

  // Form state
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedMedication, setSelectedMedication] = useState("");
  const [seedResolved, setSeedResolved] = useState(true);

  // Run state
  const [runState, setRunState] = useState<RunState>("idle");
  const [orderId, setOrderId] = useState<string | null>(null);
  const [transitionResults, setTransitionResults] = useState<
    HarnessTransitionResult[]
  >([]);
  const [diagnostics, setDiagnostics] =
    useState<HarnessDiagnosticsResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  function validate(): boolean {
    const errors: Record<string, string> = {};

    if (!selectedUserId.trim()) {
      errors.user_id = "User ID is required";
    } else if (!isValidUuid(selectedUserId)) {
      errors.user_id = "User ID must be a valid UUID";
    }

    if (!selectedMedication) {
      errors.medication = "Select a medication";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }

  // ---------------------------------------------------------------------------
  // Orchestration flow
  // ---------------------------------------------------------------------------

  const runHappyPath = useCallback(async () => {
    if (!validate()) return;

    setRunState("running");
    setErrorMessage(null);
    setTransitionResults([]);
    setDiagnostics(null);
    setOrderId(null);

    try {
      // Step 1: Create fixture order
      const orderResp: CreateHarnessOrderResponse =
        await createOrderMutation.mutateAsync({
          user_id: selectedUserId,
          medication_name_original: selectedMedication,
          seed_resolved: seedResolved,
        });

      const newOrderId = orderResp.order_id;
      if (!newOrderId) {
        throw new Error("No order_id returned from create-order");
      }
      setOrderId(newOrderId);

      // Step 2: Run sequential transitions
      const transitionsResp: RunHarnessTransitionsResponse =
        await runTransitionsMutation.mutateAsync({
          orderId: newOrderId,
          input: {
            steps: HAPPY_PATH_STEPS,
            stop_on_error: true,
          },
        });

      const results: HarnessTransitionResult[] =
        transitionsResp.results ?? [];
      setTransitionResults(results);

      const hasFailed = results.some((r) => r.status === "failed");

      // Step 3: Fetch diagnostics (best-effort)
      try {
        const diag = await diagnosticsMutation.mutateAsync(newOrderId);
        setDiagnostics(diag);
      } catch {
        // Diagnostics are best-effort; don't fail the whole run
      }

      if (hasFailed) {
        const failedStep = results.find((r) => r.status === "failed");
        setErrorMessage(
          `Transition "${failedStep?.step}" failed: ${failedStep?.error ?? "unknown error"}`
        );
        setRunState("failed");
      } else {
        setRunState("success");
      }
    } catch (err: any) {
      const msg =
        err?.data?.message ?? err?.message ?? "Unknown error occurred";
      setErrorMessage(msg);
      setRunState("failed");
    }
  }, [
    selectedUserId,
    selectedMedication,
    seedResolved,
    createOrderMutation,
    runTransitionsMutation,
    diagnosticsMutation,
  ]);

  // ---------------------------------------------------------------------------
  // Reset — clears both form inputs and execution artifacts
  // ---------------------------------------------------------------------------

  function resetRun() {
    setSelectedUserId("");
    setSelectedMedication("");
    setSeedResolved(true);
    setRunState("idle");
    setOrderId(null);
    setTransitionResults([]);
    setDiagnostics(null);
    setErrorMessage(null);
    setValidationErrors({});
  }

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const canRun =
    !isRunning && selectedUserId.trim() !== "" && selectedMedication !== "";

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      {/* Header */}
      <h1 className="text-2xl font-bold text-brown-900">
        Medication Ordering Harness
      </h1>
      <p className="mt-1 text-sm text-brown-600">
        Run medication-order lifecycle tests against the dev harness endpoints.
        Local / non-prod only.
      </p>

      {/* Form Card */}
      <div className="mt-6 space-y-5 rounded-xl border border-cream-200 bg-white p-5 shadow-sm">
        {/* User Picker */}
        <UserPicker
          userId={selectedUserId}
          onSelect={setSelectedUserId}
          disabled={isRunning}
          userSearchMutation={userSearchMutation}
        />
        {validationErrors.user_id && (
          <p className="text-xs text-red-600">{validationErrors.user_id}</p>
        )}

        {/* Medication Chips */}
        <MedicationChips
          selected={selectedMedication}
          onSelect={setSelectedMedication}
          disabled={isRunning}
        />
        {validationErrors.medication && (
          <p className="text-xs text-red-600">
            {validationErrors.medication}
          </p>
        )}

        {/* Seed Resolved Toggle */}
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-brown-700">
            <input
              type="checkbox"
              checked={seedResolved}
              onChange={(e) => setSeedResolved(e.target.checked)}
              disabled={isRunning}
              className="h-4 w-4 rounded border-cream-300 text-terracotta-600 focus:ring-terracotta-500"
            />
            Seed resolved RxNorm fields
          </label>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 border-t border-cream-200 pt-4">
          <button
            type="button"
            className="rounded-lg bg-terracotta-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-terracotta-700 disabled:opacity-60"
            onClick={runHappyPath}
            disabled={!canRun}
          >
            {isRunning ? "Running…" : "Run Happy Path"}
          </button>
          <button
            type="button"
            className="rounded-lg border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm text-brown-700 hover:bg-cream-100 disabled:opacity-60"
            onClick={resetRun}
            disabled={isRunning}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {errorMessage && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-red-800">Run failed</p>
            <p className="mt-0.5 break-all text-sm text-red-700">
              {errorMessage}
            </p>
          </div>
        </div>
      )}

      {/* Run Summary */}
      {orderId && (
        <div className="mt-6 rounded-xl border border-cream-200 bg-white p-5 shadow-sm">
          {/* Summary header */}
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-brown-900">
              Run Summary
            </h2>
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
                runState === "success" &&
                  "bg-emerald-50 text-emerald-700",
                runState === "failed" && "bg-red-50 text-red-700",
                runState === "running" &&
                  "bg-amber-50 text-amber-700"
              )}
            >
              {runState === "success" && (
                <CheckCircleIcon className="h-3.5 w-3.5" />
              )}
              {runState === "failed" && (
                <XCircleIcon className="h-3.5 w-3.5" />
              )}
              {runState}
            </span>
          </div>

          {/* Order ID */}
          <div className="mb-4 rounded-lg border border-cream-200 bg-cream-50 p-3">
            <p className="text-xs text-brown-500">Order ID</p>
            <div className="flex items-center gap-1">
              <code className="font-mono text-sm text-brown-700">
                {orderId}
              </code>
              <button
                type="button"
                className="text-brown-400 hover:text-brown-600"
                onClick={() => copyToClipboard(orderId)}
              >
                <ClipboardDocumentIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Transition Timeline */}
          {transitionResults.length > 0 && (
            <TransitionTimeline results={transitionResults} />
          )}

          {/* Diagnostics */}
          {diagnostics && (
            <div className="mt-6 border-t border-cream-200 pt-4">
              <DiagnosticsPanel diagnostics={diagnostics} orderId={orderId} />
            </div>
          )}
        </div>
      )}

      {/* Queue Health */}
      <div className="mt-6 rounded-xl border border-cream-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-brown-900">
            Queue Health
          </h2>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-cream-300 bg-cream-50 px-3 py-1.5 text-xs font-medium text-brown-700 hover:bg-cream-100 disabled:opacity-60"
            onClick={() => queueStatusQuery.refetch()}
            disabled={queueStatusQuery.isFetching}
          >
            <ArrowPathIcon
              className={`h-3.5 w-3.5 ${queueStatusQuery.isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>

        {queueStatusQuery.isLoading ? (
          <p className="mt-3 text-sm text-brown-500">
            Loading queue status…
          </p>
        ) : queueStatusQuery.isError ? (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            Queue status unavailable — the backend endpoint may not be
            deployed yet.
          </div>
        ) : queueStatusQuery.data ? (
          <div className="mt-3 space-y-3">
            <div className="flex items-center gap-2 text-sm text-brown-700">
              <BooleanIcon
                value={queueStatusQuery.data.configured}
                showLabel
                trueLabel="Configured"
                falseLabel="Not configured"
                size="sm"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-cream-50 p-3">
                <p className="text-xs text-brown-500">Messages visible</p>
                <p className="mt-1 text-lg font-semibold text-brown-900">
                  {queueStatusQuery.data.messages_visible}
                </p>
              </div>
              <div className="rounded-lg bg-cream-50 p-3">
                <p className="text-xs text-brown-500">In flight</p>
                <p className="mt-1 text-lg font-semibold text-brown-900">
                  {queueStatusQuery.data.messages_in_flight}
                </p>
              </div>
              <div className="rounded-lg bg-cream-50 p-3">
                <p className="text-xs text-brown-500">DLQ visible</p>
                <p className="mt-1 text-lg font-semibold text-brown-900">
                  {queueStatusQuery.data.dlq_messages_visible}
                </p>
              </div>
            </div>

            <p className="text-xs text-brown-400">
              Checked at{" "}
              {new Date(queueStatusQuery.data.checked_at).toLocaleString()}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
